import type { AnomalyDetail } from '../types/weld';

const ML_LABEL_VALUES = new Set(['-1', '1', -1, 1]);

function isMlLabel(value: unknown): boolean {
  return ML_LABEL_VALUES.has(value as string | number);
}

function firstSentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  const match = trimmed.match(/^[^.!?]+[.!?]?/);
  return (match?.[0] ?? trimmed).trim();
}

const SOURCE_LABELS: Record<string, string> = {
  'WPS+ML': 'WPS and ML both flagged an issue',
  'ML Hidden': 'ML flagged a possible hidden issue',
  WPS: 'WPS limits were exceeded',
  ML: 'ML flagged an unusual pattern',
  Normal: '',
};

/** Friendly label for anomaly_source codes shown in subtitles. */
export function formatAnomalySourceLabel(source?: string | null): string {
  if (!source?.trim()) return 'WeldIQ';
  return SOURCE_LABELS[source.trim()] ?? source.trim();
}

/** User-facing title — never exposes raw ML flags (-1 / 1). */
export function formatAnomalyDisplayTitle(anomaly: AnomalyDetail): string {
  const summary = anomaly.plain_english_summary?.trim();
  if (summary) {
    return firstSentence(summary);
  }

  const businessClass = anomaly.business_class?.trim();
  if (businessClass && businessClass.toLowerCase() !== 'normal') {
    return businessClass;
  }

  const sourceLabel = formatAnomalySourceLabel(anomaly.anomaly_source);
  if (sourceLabel && sourceLabel !== 'WeldIQ') {
    return sourceLabel;
  }

  const label = anomaly.anomaly_label;
  if (label != null && !isMlLabel(label)) {
    const text = String(label).trim();
    if (text) return text;
  }

  return 'Unusual welding pattern';
}

/** Summary body when it adds detail beyond the title. */
export function formatAnomalyDisplaySummary(
  anomaly: AnomalyDetail,
  title: string,
): string | null {
  const summary = anomaly.plain_english_summary?.trim();
  if (!summary) return null;
  const normalizedTitle = title.trim().toLowerCase();
  const normalizedSummary = summary.toLowerCase();
  if (normalizedSummary === normalizedTitle) return null;
  if (normalizedSummary.startsWith(normalizedTitle.replace(/\.$/, ''))) {
    const rest = summary.slice(title.length).trim().replace(/^[.!?]\s*/, '');
    return rest || null;
  }
  return summary;
}
